import { useEffect, useRef, useState } from "react";
import { apiClient } from "../../../../utils/Api";
import { checkAuthorized, manipulateErrors } from "../../../../utils";

const useFetchProducts = (props) => {
    const {
        keyword,
        status,
        condition,
        size,
        dataPerPage = 10,
        sort = "updatedAt_desc",
        showExpiredModal,
    } = props;
    const [perPage, setPerPage] = useState(dataPerPage);
    const [isError, setIsError] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [products, setProducts] = useState([]);
    const [response, setResponse] = useState();
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [statusCode, setStatusCode] = useState();
    const requestIdRef = useRef(0);
    const isMountedRef = useRef(true);
    const isFetchingRef = useRef(false);

    const handleParamsStatus = (params) => {
        if (status === "consignment") {
            Object.assign(params, { pre_verified: 1, pre_order: 0 });
        }

        if (status === "pre_order") {
            Object.assign(params, { pre_order: 1, pre_verified: 0 });
        }

        if (status === "standard") {
            Object.assign(params, { pre_verified: 0, pre_order: 0 });
        }
    };

    const removeEmptyParams = (params) => {
        return Object.entries(params).reduce((result, [key, value]) => {
            if (value === "" || value == null) {
                return result;
            }

            return {
                ...result,
                [key]: value,
            };
        }, {});
    };

    const buildInitialParams = () => {
        const params = {
            availables: true,
            keyword,
            sneakers_condition: condition,
            size,
            per_page: perPage,
            sort_by: sort,
            without_append: true,
        };

        handleParamsStatus(params);

        return removeEmptyParams(params);
    };

    const query = (params) => {
        return apiClient()
            .get("/admins/v2/sells", { params })
            .then((response) => {
                return {
                    isSuccess: true,
                    statusCode: response.status,
                    response: response.data,
                };
            })
            .catch((err) => {
                if (!checkAuthorized(manipulateErrors(err))) {
                    showExpiredModal();
                }
                const response = err.response;
                return {
                    isSuccess: false,
                    statusCode: response?.status || 500,
                    response: response?.data || null,
                };
            });
    };

    const fetch = async (customParams = null, append = false) => {
        if (isFetchingRef.current) {
            return;
        }

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        isFetchingRef.current = true;
        setIsFetching(true);

        const params = customParams?.cursor
            ? { cursor: customParams.cursor }
            : buildInitialParams();

        const fetchQuery = await query(params);

        if (
            !isMountedRef.current ||
            requestId !== requestIdRef.current
        ) {
            return;
        }

        if (!fetchQuery.isSuccess) {
            setIsError(true);
            setStatusCode(fetchQuery.statusCode);
            setResponse(fetchQuery.response);
            if (!append) {
                setProducts([]);
            }
            setHasMore(false);
            setNextCursor(null);
            setIsFetching(false);
            isFetchingRef.current = false;
            return;
        }

        const nextProducts = Array.isArray(fetchQuery.response?.data)
            ? fetchQuery.response.data
            : [];

        setIsError(false);
        setStatusCode(fetchQuery.statusCode);
        setResponse(fetchQuery.response);
        setProducts((previousProducts) =>
            append ? [...previousProducts, ...nextProducts] : nextProducts
        );
        setHasMore(Boolean(fetchQuery.response?.has_more));
        setNextCursor(fetchQuery.response?.next_cursor || null);
        setIsFetching(false);
        isFetchingRef.current = false;
    };

    const fetchNextPage = () => {
        if (!nextCursor || !hasMore) {
            return;
        }

        fetch({ cursor: nextCursor }, true);
    };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
            isFetchingRef.current = false;
        };
    }, []);

    return {
        isError,
        isFetching,
        products,
        response,
        perPage,
        setPerPage,
        reFetch: fetch,
        fetchNextPage,
        hasMore,
        nextCursor,
        setProducts,
        setResponse,
        setHasMore,
        setNextCursor,
        statusCode,
    };
};

export default useFetchProducts;
